import {
  entitiesListResponseSchema,
  entityDetailResponseSchema,
  financialsResponseSchema,
  marketOpportunityResponseSchema,
  marketsResponseSchema,
  type EntitiesListResponse,
  type EntityDetailResponse,
  type FinancialsResponse,
  type MarketOpportunityResponse,
  type MarketsResponse,
  type MarketCode,
  type SubSegment,
  type ScopeFlag,
  type BoltOnClass,
  type CatchmentMode,
  type TravelMode,
  type Condition,
} from '@hc/shared';
import { z } from 'zod';
import { recordApiError } from '../diagnostics.js';

// Deployed: same-origin /api through the Cloudflare worker; local: VITE_API_URL.
import { API_BASE_URL } from './base-url.js';

// Auth token getter — set once by AuthProvider after Clerk is ready.
// In open-dev mode it stays null and the backend's preHandler grants
// the synthetic dev-user identity from the request.
let authTokenGetter: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: (() => Promise<string | null>) | null): void {
  authTokenGetter = getter;
}

/** The Authorization header for a request made outside `request()`. The API
 *  refuses every data route without it. */
export async function authHeaders(): Promise<Record<string, string>> {
  const token = authTokenGetter ? await authTokenGetter() : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// Client-side concurrency gate. A panel-dense view (the Landing workspace fires
// ~20 requests as it mounts) would otherwise hit the API all at once; the heavy
// ones (facilities / market-opportunity / facets, 20–40s each) then pin every
// DB pool connection and everything — even the fast calls — queues behind them,
// cascading into the 120s-timeout wedge we saw on cold load. Capping in-flight
// requests here keeps at most N at the server, so the pool always has headroom
// and queued calls run as slots free. Server pool is 16 (DB_MAX_CONNECTIONS);
// 6 leaves ample headroom while keeping the UI parallel. FIFO, so no request
// starves. The server-side SET LOCAL read ceiling (@hc/db withReadTimeout) is
// the complementary backstop: a slot is never held longer than that ceiling.
const MAX_CONCURRENT_REQUESTS = 6;
let activeRequests = 0;
const requestQueue: Array<() => void> = [];

function acquireRequestSlot(): Promise<void> {
  if (activeRequests < MAX_CONCURRENT_REQUESTS) {
    activeRequests++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => requestQueue.push(resolve));
}

function releaseRequestSlot(): void {
  const next = requestQueue.shift();
  // Hand the slot straight to the next waiter (active count unchanged); only
  // decrement when nobody is waiting.
  if (next) next();
  else activeRequests--;
}

// Typed fetch wrapper. The response is validated against a zod schema from
// @hc/shared — so a wire-shape drift between server and client surfaces as
// a parse error in tests / runtime rather than `undefined` deep in a panel.
async function request<T>(
  path: string,
  schema: { parse: (data: unknown) => T },
  init?: RequestInit,
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  // Resolve the auth token BEFORE taking a slot — it's a cached Clerk call, not
  // an API-pool request, and shouldn't consume gate capacity.
  const token = authTokenGetter ? await authTokenGetter() : null;
  await acquireRequestSlot();
  try {
    const res = await fetch(url, {
      ...init,
      headers: {
        // Only advertise a JSON body when there actually is one. Sending
        // `Content-Type: application/json` on a body-less request (e.g. a
        // DELETE) makes Fastify's JSON parser reject the empty body with
        // 400 FST_ERR_CTP_EMPTY_JSON_BODY — which silently broke watchlist
        // removal (untick) across the app.
        ...(init?.body != null ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init?.headers,
      },
    });
    if (!res.ok) {
      let body: unknown = undefined;
      try {
        body = await res.json();
      } catch {
        // body is non-JSON, ignore
      }
      recordApiError({ path, status: res.status, statusText: res.statusText, body });
      throw new ApiError(`${res.status} ${res.statusText} at ${path}`, res.status, body);
    }
    const json: unknown = await res.json();
    return schema.parse(json);
  } finally {
    releaseRequestSlot();
  }
}

// --- markets ---

export function getMarkets(): Promise<MarketsResponse> {
  return request('/markets', marketsResponseSchema);
}

// --- market / opportunity ---

/** Uniform NUTS region grain (migrations 0077-0079). */
export type RegionLevel = 'legacy' | 'nuts1' | 'nuts2' | 'nuts3';

export function getMarketOpportunity(
  country?: MarketCode | readonly MarketCode[],
  altitude?: 'region' | 'country',
  level?: RegionLevel,
): Promise<MarketOpportunityResponse> {
  const params = new URLSearchParams();
  if (country) {
    params.set('country', Array.isArray(country) ? country.join(',') : String(country));
  }
  if (altitude) params.set('altitude', altitude);
  if (level) params.set('level', level);
  const qs = params.toString();
  return request(`/market/opportunity${qs ? `?${qs}` : ''}`, marketOpportunityResponseSchema);
}

// --- origination opportunity score ---

export interface OriginationComponent {
  f: number | null;
  c: number;
}
export interface OriginationCell {
  region: string;
  subsegment: string;
  nTotal: number;
  fragmentation: OriginationComponent;
  whitespace: OriginationComponent;
  succession: OriginationComponent;
  acquisition: OriginationComponent;
  momentum: OriginationComponent;
  demand: OriginationComponent;
  workforce: OriginationComponent;
}
export interface OriginationResponse {
  cells: OriginationCell[];
  countries: string[];
}

// --- Corryk Origination Signal (entity-level) ---
// Distinct from the region-level Origination Opportunity / Corryk Opportunity Index above.

export type SignalConfidence = 'High' | 'Moderate' | 'Indicative';

export const originationSignalRowSchema = z.object({
  entityId: z.string(),
  name: z.string().nullable(),
  registryId: z.string(),
  country: z.string(),
  region: z.string().nullable(),
  subsegment: z.string().nullable(),
  originationSignal: z.number().nullable(),
  signalConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullable(),
  note: z.string().nullable(),
  // nullish (not nullable) so the web tolerates an older API that doesn't yet
  // return these fields during the deploy window.
  sellerReadiness: z.number().nullish(),
  sellerReadinessConfidence: z.enum(['High', 'Moderate', 'Indicative']).nullish(),
  sellerNote: z.string().nullish(),
  revenueEur: z.number().nullable(),
  revenueConfidence: z.string().nullish(),
});
export type OriginationSignalRow = z.infer<typeof originationSignalRowSchema>;

export const originationSignalResponseSchema = z.object({
  entities: z.array(originationSignalRowSchema),
  aboveBarTotal: z.number(),
  countries: z.array(z.string()),
  pageSize: z.number(),
  offset: z.number(),
});
export type OriginationSignalResponse = z.infer<typeof originationSignalResponseSchema>;

export interface OriginationSignalQuery {
  country?: string;
  subsegment?: string[];
  region?: string[];
  confidence?: string[];
  revenueMin?: number;
  revenueMax?: number;
  minSignal?: number;
  seed?: string;
  offset?: number;
}

export function fetchOriginationSignal(
  q: OriginationSignalQuery,
): Promise<OriginationSignalResponse> {
  const p = new URLSearchParams();
  if (q.country) p.set('country', q.country);
  if (q.subsegment?.length) p.set('subsegment', q.subsegment.join(','));
  if (q.region?.length) p.set('region', q.region.join(','));
  if (q.confidence?.length) p.set('confidence', q.confidence.join(','));
  if (q.revenueMin != null) p.set('revenueMin', String(q.revenueMin));
  if (q.revenueMax != null) p.set('revenueMax', String(q.revenueMax));
  if (q.minSignal != null) p.set('minSignal', String(q.minSignal));
  if (q.seed) p.set('seed', q.seed);
  if (q.offset != null) p.set('offset', String(q.offset));
  const qs = p.toString();
  return request(`/origination-signal${qs ? `?${qs}` : ''}`, originationSignalResponseSchema);
}

export function fetchOrigination(
  country?: MarketCode | readonly MarketCode[],
  level?: RegionLevel,
): Promise<OriginationResponse> {
  const params = new URLSearchParams();
  if (country) params.set('country', Array.isArray(country) ? country.join(',') : String(country));
  if (level) params.set('level', level);
  const qs = params.toString();
  return request(`/origination${qs ? `?${qs}` : ''}`, { parse: (r) => r as OriginationResponse });
}

// --- reimbursement margin cross-check ---
//
// The observed-margin counterpart to the curated reimbursement reference: per
// (country × subsegment bucket) the median YoY operating-margin change (bps)
// across filed P&Ls. UK filers mostly file abbreviated accounts (no P&L), so in
// practice this is FR-heavy; buckets below the min sample are simply absent.

export interface ReimbursementMarginCell {
  country: string;
  bucket: string;
  medianDeltaBps: number;
  n: number;
}
export interface ReimbursementMarginsResponse {
  margins: ReimbursementMarginCell[];
  minSample: number;
}
export function getReimbursementMargins(): Promise<ReimbursementMarginsResponse> {
  return request('/market/reimbursement/margins', {
    parse: (raw) => raw as ReimbursementMarginsResponse,
  });
}

// --- entities ---

export interface EntitiesQuery {
  // country + subSegment accept either a single code or an array of
  // codes; the client serialises arrays as a comma-separated string
  // ("FR,BE") and the API parses them back via the csvList zod helper.
  country?: MarketCode | readonly MarketCode[];
  subSegment?: SubSegment | readonly SubSegment[];
  scopeFlag?: ScopeFlag;
  /** Free-text search across legal_name + registry_id. */
  q?: string;
  /** Include entities marked rejected (default false — suppressed from
   *  discovery surfaces per interface-brief Section 5.7). */
  includeRejected?: boolean;
  limit?: number;
  offset?: number;

  // --- Range filters (Screener) -------------------------------------
  //
  // Each pair narrows the candidate set by the latest filed financial
  // period (or registration date for yearsActive, or the y/y growth
  // between the two most-recent periods for the growth filters). Set
  // only the active edge — leaving min/max undefined means "no bound
  // on that side", matching the "at-max sentinel" semantic.
  revenueMin?: number;
  revenueMax?: number;
  totalAssetsMin?: number;
  totalAssetsMax?: number;
  netAssetsMin?: number;
  netAssetsMax?: number;
  currentLiabilitiesMin?: number;
  currentLiabilitiesMax?: number;
  employeesMin?: number;
  employeesMax?: number;
  yearsActiveMin?: number;
  yearsActiveMax?: number;
  /** Percent (e.g. 25 = 25 %), not decimal. */
  revenueGrowthMin?: number;
  revenueGrowthMax?: number;
  /** Percent (e.g. 25 = 25 %), not decimal. */
  employeeGrowthMin?: number;
  employeeGrowthMax?: number;
}

const RANGE_PARAM_KEYS = [
  'revenueMin',
  'revenueMax',
  'totalAssetsMin',
  'totalAssetsMax',
  'netAssetsMin',
  'netAssetsMax',
  'currentLiabilitiesMin',
  'currentLiabilitiesMax',
  'employeesMin',
  'employeesMax',
  'yearsActiveMin',
  'yearsActiveMax',
  'revenueGrowthMin',
  'revenueGrowthMax',
  'employeeGrowthMin',
  'employeeGrowthMax',
] as const satisfies ReadonlyArray<keyof EntitiesQuery>;

export function listEntities(query: EntitiesQuery = {}): Promise<EntitiesListResponse> {
  const params = new URLSearchParams();
  if (query.country) {
    params.set(
      'country',
      Array.isArray(query.country) ? query.country.join(',') : (query.country as string),
    );
  }
  if (query.subSegment) {
    params.set(
      'subSegment',
      Array.isArray(query.subSegment) ? query.subSegment.join(',') : (query.subSegment as string),
    );
  }
  if (query.scopeFlag) params.set('scopeFlag', query.scopeFlag);
  if (query.q) params.set('q', query.q);
  if (query.includeRejected) params.set('includeRejected', 'true');
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  for (const k of RANGE_PARAM_KEYS) {
    const v = query[k];
    if (v !== undefined) params.set(k, String(v));
  }
  const qs = params.toString();
  return request(`/entities${qs ? `?${qs}` : ''}`, entitiesListResponseSchema);
}

// Batch lookup — used by the watchlist bar to resolve a small set of
// pinned entity ids to {id, legalName, country, registryId, subSegment,
// scopeFlag} in one round trip. Server caps at 200 ids per call.
export function getEntitiesByIds(ids: string[]): Promise<EntitiesListResponse> {
  if (ids.length === 0)
    return Promise.resolve({
      data: [],
      pagination: { total: 0, limit: 0, offset: 0 },
    } as unknown as EntitiesListResponse);
  const params = new URLSearchParams();
  params.set('ids', ids.join(','));
  // The /entities/by-ids endpoint omits the pagination block but the
  // listed response schema requires it; build a minimal payload via a
  // direct fetch + small projection.
  return request(`/entities/by-ids?${params.toString()}`, {
    parse: (data: unknown) => {
      const obj = data as { data: unknown[] };
      return {
        data: obj.data,
        pagination: { total: obj.data.length, limit: obj.data.length, offset: 0 },
      } as EntitiesListResponse;
    },
  });
}

export function getEntity(id: string): Promise<EntityDetailResponse> {
  return request(`/entities/${id}`, entityDetailResponseSchema);
}

export function getEntityFinancials(id: string): Promise<FinancialsResponse> {
  return request(`/entities/${id}/financials`, financialsResponseSchema);
}

// --- per-entity signals + directors ------------------------------------
//
// The shared package doesn't have schemas for these yet; they live behind
// untyped fetches with structural casts. Tighten when the panels need
// richer types than the inline shape.

export interface EntitySignalRow {
  archetype: string;
  score: string;
  coverage: string;
  evidence: Array<{
    ruleId: string;
    weight: number;
    value: number;
    contribution: number;
    notes?: string;
  }>;
  computedAt: string;
}
export function getEntitySignals(id: string): Promise<{ data: EntitySignalRow[] }> {
  return request(`/entities/${id}/signals`, {
    parse: (raw) => raw as { data: EntitySignalRow[] },
  });
}

// --- per-user saved workspaces -------------------------------------

export interface WorkspaceRow {
  id: string;
  name: string;
  description: string | null;
  isDefault: string | null;
  updatedAt: string;
}

export interface WorkspaceDetail extends WorkspaceRow {
  layoutJson: Record<string, unknown>;
  createdAt: string;
  userId: string;
}

export function listWorkspaces(): Promise<{ data: WorkspaceRow[] }> {
  return request('/workspaces', { parse: (raw) => raw as { data: WorkspaceRow[] } });
}

export function getWorkspace(id: string): Promise<{ data: WorkspaceDetail }> {
  return request(`/workspaces/${id}`, { parse: (raw) => raw as { data: WorkspaceDetail } });
}

export function createWorkspace(input: {
  name: string;
  description?: string;
  layoutJson: Record<string, unknown>;
  isDefault?: boolean;
}): Promise<{ data: WorkspaceDetail }> {
  return request(
    '/workspaces',
    { parse: (raw) => raw as { data: WorkspaceDetail } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function updateWorkspace(
  id: string,
  input: {
    name?: string;
    description?: string;
    layoutJson?: Record<string, unknown>;
    isDefault?: boolean;
  },
): Promise<{ data: WorkspaceDetail }> {
  return request(
    `/workspaces/${id}`,
    { parse: (raw) => raw as { data: WorkspaceDetail } },
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function deleteWorkspace(id: string): Promise<{ ok: true }> {
  return request(
    `/workspaces/${id}`,
    { parse: (raw) => raw as { ok: true } },
    { method: 'DELETE' },
  );
}

// --- screener presets ----------------------------------------------

export interface ScreenerPresetRow {
  id: string;
  userId: string;
  name: string;
  filterJson: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export function listScreenerPresets(): Promise<{ data: ScreenerPresetRow[] }> {
  return request('/screener-presets', {
    parse: (raw) => raw as { data: ScreenerPresetRow[] },
  });
}

export function createScreenerPreset(input: {
  name: string;
  filterJson: Record<string, unknown>;
}): Promise<{ data: ScreenerPresetRow }> {
  return request(
    '/screener-presets',
    { parse: (raw) => raw as { data: ScreenerPresetRow } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function updateScreenerPreset(
  id: string,
  input: { name?: string; filterJson?: Record<string, unknown> },
): Promise<{ data: ScreenerPresetRow }> {
  return request(
    `/screener-presets/${id}`,
    { parse: (raw) => raw as { data: ScreenerPresetRow } },
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function deleteScreenerPreset(id: string): Promise<{ ok: true }> {
  return request(
    `/screener-presets/${id}`,
    { parse: (raw) => raw as { ok: true } },
    { method: 'DELETE' },
  );
}

// ── Buyer mandates — per-user BD strategy (Phase 2) ──────────────────────────
export interface BuyerMandateRow {
  id: string;
  userId: string;
  name: string;
  countries: string[];
  subsegments: string[];
  specialties: string[];
  clinicalSpecialisms: string[];
  regions: string[];
  appetite: string[];
  involvement: string[];
  management: string[];
  revMinEur: number | null;
  revMaxEur: number | null;
  noGos: string[];
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The mutable strategy fields — the create payload; update takes a Partial. */
export type BuyerMandateInput = Omit<BuyerMandateRow, 'id' | 'userId' | 'createdAt' | 'updatedAt'>;

export function listBuyerMandates(): Promise<{ data: BuyerMandateRow[] }> {
  return request('/buyer-mandates', { parse: (raw) => raw as { data: BuyerMandateRow[] } });
}

export function createBuyerMandate(input: BuyerMandateInput): Promise<{ data: BuyerMandateRow }> {
  return request(
    '/buyer-mandates',
    { parse: (raw) => raw as { data: BuyerMandateRow } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function updateBuyerMandate(
  id: string,
  input: Partial<BuyerMandateInput>,
): Promise<{ data: BuyerMandateRow }> {
  return request(
    `/buyer-mandates/${id}`,
    { parse: (raw) => raw as { data: BuyerMandateRow } },
    { method: 'PUT', body: JSON.stringify(input) },
  );
}

export function deleteBuyerMandate(id: string): Promise<{ ok: true }> {
  return request(
    `/buyer-mandates/${id}`,
    { parse: (raw) => raw as { ok: true } },
    { method: 'DELETE' },
  );
}

// ── Buyer portfolios — named holding containers (conditions layer, §16) ──────
export interface BuyerPortfolioRow {
  id: string;
  name: string;
  notes: string | null;
  createdAt: string;
  holdingsCount: number;
}

export function listBuyerPortfolios(): Promise<{ data: BuyerPortfolioRow[] }> {
  return request('/buyer-portfolios', { parse: (raw) => raw as { data: BuyerPortfolioRow[] } });
}

export function createBuyerPortfolio(input: {
  name: string;
  notes?: string | null;
}): Promise<{ data: BuyerPortfolioRow }> {
  return request(
    '/buyer-portfolios',
    { parse: (raw) => raw as { data: BuyerPortfolioRow } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function updateBuyerPortfolio(
  id: string,
  patch: { name?: string; notes?: string | null },
): Promise<{ data: BuyerPortfolioRow }> {
  return request(
    `/buyer-portfolios/${id}`,
    { parse: (raw) => raw as { data: BuyerPortfolioRow } },
    { method: 'PUT', body: JSON.stringify(patch) },
  );
}

/** Deletes the portfolio AND its holdings (FK cascade) — warn before calling. */
export function deleteBuyerPortfolio(id: string): Promise<{ ok: true }> {
  return request(
    `/buyer-portfolios/${id}`,
    { parse: (raw) => raw as { ok: true } },
    { method: 'DELETE' },
  );
}

// ── Buyer portfolio — owned holdings (Phase 2) ───────────────────────────────
export interface BuyerHoldingRow {
  id: string;
  /** The named portfolio this holding belongs to. */
  portfolioId: string;
  entityId: string | null;
  name: string | null;
  countryCode: string | null;
  subsegment: string | null;
  region: string | null;
  postcode: string | null;
  latitude: number | null;
  longitude: number | null;
  turnoverEur: number | null;
  sites: number | null;
  notes: string | null;
  /** True = linked to a DB entity (fields resolved live); false = manual entry. */
  linked: boolean;
}

/** Create payload — either { entityId } (DB link) or manual fields (name + …).
 *  portfolioId omitted = the user's default portfolio (server fallback). */
export interface BuyerHoldingInput {
  portfolioId?: string | null;
  entityId?: string | null;
  name?: string | null;
  countryCode?: string | null;
  subsegment?: string | null;
  region?: string | null;
  postcode?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  turnoverEur?: number | null;
  sites?: number | null;
  notes?: string | null;
}

export function listBuyerHoldings(portfolioId?: string): Promise<{ data: BuyerHoldingRow[] }> {
  const qs = portfolioId ? `?portfolioId=${encodeURIComponent(portfolioId)}` : '';
  return request(`/buyer-holdings${qs}`, {
    parse: (raw) => raw as { data: BuyerHoldingRow[] },
  });
}

export function createBuyerHolding(input: BuyerHoldingInput): Promise<{ data: BuyerHoldingRow }> {
  return request(
    '/buyer-holdings',
    { parse: (raw) => raw as { data: BuyerHoldingRow } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function deleteBuyerHolding(id: string): Promise<{ ok: true }> {
  return request(
    `/buyer-holdings/${id}`,
    { parse: (raw) => raw as { ok: true } },
    { method: 'DELETE' },
  );
}

export interface TelemetrySnapshot {
  lastCompute: Array<{ countryCode: string; lastEvaluationDate: string | null }>;
  queueDepth: number | null;
  sources: Array<{ source: string; countryCode: string; lastRunAt: string | null }>;
}
export function getTelemetry(): Promise<TelemetrySnapshot> {
  return request('/telemetry', {
    parse: (raw) => raw as TelemetrySnapshot,
  });
}

export interface FeedbackInput {
  area: 'general' | 'data' | 'workflow' | 'performance' | 'bug' | 'feature';
  category: 'bug' | 'performance' | 'feature_request' | 'data_issue' | 'ux_confusion';
  severity: 'blocker' | 'painful' | 'minor';
  description: string;
  expected?: string | null;
  contextLabel?: string | null;
  diagnosticsIncluded: boolean;
  diagnostics?: Record<string, unknown> | null;
  userEmail?: string | null;
  userName?: string | null;
}

export function submitFeedback(
  input: FeedbackInput,
): Promise<{ data: { id: string; createdAt: string } }> {
  return request(
    '/feedback',
    { parse: (raw) => raw as { data: { id: string; createdAt: string } } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export interface EntityAggregateRow {
  id: string;
  revenueTrend: number[];
  firedSignalCount: number;
}
export function getEntityAggregates(ids: string[]): Promise<{ data: EntityAggregateRow[] }> {
  if (ids.length === 0) return Promise.resolve({ data: [] });
  const params = new URLSearchParams({ ids: ids.join(',') });
  return request(`/entities/aggregates?${params.toString()}`, {
    parse: (raw) => raw as { data: EntityAggregateRow[] },
  });
}

export interface EntitySignalEvaluationRow {
  signalId: string;
  family: string;
  fired: boolean;
  score: number | null;
  confidence: number | null;
  joinKeyUsed: string | null;
  evidence: unknown;
  peInterpretation: string | null;
  evaluationDate: string;
  computedAt: string;
}
export function getEntitySignalEvaluations(
  id: string,
): Promise<{ data: EntitySignalEvaluationRow[] }> {
  return request(`/entities/${id}/signal-evaluations`, {
    parse: (raw) => raw as { data: EntitySignalEvaluationRow[] },
  });
}

export interface EntityDirectorRow {
  id: string;
  role: string;
  appointedAt: string | null;
  resignedAt: string | null;
  birthYearBucket: string | null;
  countryCode: string;
  isSuppressed: boolean;
}
export function getEntityDirectors(id: string): Promise<{ data: EntityDirectorRow[] }> {
  return request(`/entities/${id}/directors`, {
    parse: (raw) => raw as { data: EntityDirectorRow[] },
  });
}

// --- Company Overview supporting payloads ---------------------------
//
// Lazy-loaded per-entity feeds consumed by sections of the Company
// Overview panel. Mirror the /entities/:id/* endpoints in
// apps/api/src/routes/entities.ts; see company-overview-brief.md §4-5.

export interface EntityActivityEventRow {
  id: string;
  /** Free-text tag normalised at ingest. See corporate_events.event_type. */
  eventType: string;
  effectiveDate: string;
  publicationDate: string | null;
  headline: string | null;
  source: string;
  sourceUrl: string | null;
}
export function getEntityActivity(id: string): Promise<{ data: EntityActivityEventRow[] }> {
  return request(`/entities/${id}/activity`, {
    parse: (raw) => raw as { data: EntityActivityEventRow[] },
  });
}

export interface EntityCqcLocation {
  locationId: string;
  providerId: string | null;
  name: string | null;
  serviceTypes: string[];
  regulatedActivities: string[];
  latestRating: string | null;
  latestInspectionDate: string | null;
  registrationDate: string | null;
  deregistrationDate: string | null;
}
export interface EntityNhsDentalContract {
  contractNumber: string;
  contractorName: string | null;
  practiceAddress: string | null;
  periodEnd: string;
  udaDelivered: string | null;
  uoaDelivered: string | null;
  patientChargeRevenue: string | null;
  coursesOfTreatment: number | null;
  /** Annual NHS contract value, GBP — public-payer component, not revenue. */
  contractFinancialValue: string | null;
}
export interface EntityNhsGpPayment {
  practiceCode: string;
  practiceName: string | null;
  address: string | null;
  financialYearStart: string;
  totalPayments: string | null;
  listSize: number | null;
}
export interface EntityEuLicense {
  licenseId: string;
  authority: string;
  providerName: string | null;
  activities: string[];
  latestRating: string | null;
  latestInspectionDate: string | null;
  validFrom: string | null;
  validUntil: string | null;
  addressLine: string | null;
  region: string | null;
}
export interface EntityEuPayerContract {
  providerId: string;
  payer: string;
  providerName: string | null;
  contractType: string | null;
  serviceCategories: string[];
  validFrom: string;
  validUntil: string | null;
}
export interface EntityDkProductionUnit {
  pNumber: string;
  name: string | null;
  addressLine: string | null;
  city: string | null;
  postcode: string | null;
  employeeCount: number | null;
  registrationDate: string | null;
  closureDate: string | null;
}
export interface EntityFrFinessEntiteJuridique {
  finessEjId: string;
  name: string | null;
  categoryCode: string | null;
  siren: string | null;
  region: string | null;
}
export interface EntityFrFinessEtablissement {
  finessEtId: string;
  finessEjId: string | null;
  name: string | null;
  categoryCode: string | null;
  authorisedActivities: string[];
  bedCount: number | null;
  placeCount: number | null;
  addressLine: string | null;
  postcode: string | null;
  commune: string | null;
  region: string | null;
  arsRegion: string | null;
  status: string | null;
}
export interface EntityFrRppsPractitioner {
  rppsNumber: string;
  fullName: string | null;
  profession: string | null;
  specialty: string | null;
  practiceMode: string | null;
  firstRegistrationYear: number | null;
  matchConfidence: string | null;
  addressMode: string | null;
}
export interface EntityFrHasCertification {
  finessEtId: string;
  certifiedAt: string;
  rating: string | null;
  validUntil: string | null;
  reportUrl: string | null;
}
export interface EntityFrDamirReimbursementProxy {
  periodYear: number;
  subSegment: string | null;
  proxyBand: string;
  actCount: number | null;
  reimbursedAmountEur: string | null;
}
export interface EntityFrOnpPharmacy {
  onpPharmacyId: string;
  pharmacyName: string | null;
  category: string | null;
  titulaireRpps: string | null;
  addressLine: string | null;
  postcode: string | null;
  commune: string | null;
}
export interface EntityHealthcarePresence {
  cqcLocations: EntityCqcLocation[];
  nhsDentalContracts: EntityNhsDentalContract[];
  nhsGpPayments: EntityNhsGpPayment[];
  euLicenses: EntityEuLicense[];
  euPayerContracts: EntityEuPayerContract[];
  dkProductionUnits: EntityDkProductionUnit[];
  frFinessEntiteJuridique: EntityFrFinessEntiteJuridique | null;
  frFinessEtablissements: EntityFrFinessEtablissement[];
  frRppsPractitioners: EntityFrRppsPractitioner[];
  frHasCertifications: EntityFrHasCertification[];
  frDamirReimbursementProxy: EntityFrDamirReimbursementProxy[];
  frOnpPharmacy: EntityFrOnpPharmacy | null;
}
export function getEntityHealthcarePresence(
  id: string,
): Promise<{ data: EntityHealthcarePresence }> {
  return request(`/entities/${id}/healthcare-presence`, {
    parse: (raw) => raw as { data: EntityHealthcarePresence },
  });
}

export interface EntityCrossDirectorshipRow {
  entityId: string;
  legalName: string | null;
  countryCode: string;
  registryId: string;
  sharedDirectorCount: number;
}
export function getEntityCrossDirectorships(
  id: string,
): Promise<{ data: EntityCrossDirectorshipRow[] }> {
  return request(`/entities/${id}/cross-directorships`, {
    parse: (raw) => raw as { data: EntityCrossDirectorshipRow[] },
  });
}

export interface EntityMaEvent {
  publicId: string;
  eventType: string;
  eventDate: string | null;
  announcedDate: string | null;
  closeDate: string | null;
  buyerName: string | null;
  sellerName: string | null;
  targetName: string;
  dealValue: string | null;
  dealCurrency: string | null;
  sources: Array<{ url: string; publication: string | null; headline: string | null }>;
}
export function getEntityMaEvent(id: string): Promise<{ data: EntityMaEvent | null }> {
  return request(`/entities/${id}/ma-event`, {
    parse: (raw) => raw as { data: EntityMaEvent | null },
  });
}

// --- list memberships -------------------------------------------------
//
// Two concepts live here now:
//   1. Rejected list — single, per-org, suppression from discovery. Still
//      uses /lists/:kind, /entities/:id/lists/:kind with kind='rejected'.
//   2. Named watchlists — multiple per user, tagging semantics. See the
//      Watchlists block further down.

export type ListKind = 'rejected';

export type RejectionCategory =
  | 'out_of_scope'
  | 'wrong_size_or_stage'
  | 'evaluated_and_passed'
  | 'conflicted_or_restricted';

export interface ListMembershipRow {
  id: string;
  countryCode: string;
  registryId: string;
  legalName: string | null;
  subSegment: string | null;
  city: string | null;
  scopeFlag: ScopeFlag;
  addedAt: string;
  ownerId: string | null;
  rejectionCategory: RejectionCategory | null;
  latestFinancials?: {
    revenue: string | null;
    totalAssets: string | null;
    equity: string | null;
    creditors: string | null;
    employeeCount: number | null;
  } | null;
}

export function getListEntities(
  kind: ListKind,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ data: ListMembershipRow[] }> {
  const p = new URLSearchParams();
  if (opts.limit !== undefined) p.set('limit', String(opts.limit));
  if (opts.offset !== undefined) p.set('offset', String(opts.offset));
  const qs = p.toString();
  return request(`/lists/${kind}${qs ? `?${qs}` : ''}`, {
    parse: (raw) => raw as { data: ListMembershipRow[] },
  });
}

export interface EntityListMembership {
  listKind: ListKind;
  addedAt: string;
  ownerId: string | null;
  rejectionCategory: RejectionCategory | null;
}

export function getEntityLists(id: string): Promise<{ data: EntityListMembership[] }> {
  return request(`/entities/${id}/lists`, {
    parse: (raw) => raw as { data: EntityListMembership[] },
  });
}

export function transitionEntityToList(
  id: string,
  kind: ListKind,
  body: {
    rejectionCategory?: RejectionCategory;
    rationaleText?: string;
  } = {},
): Promise<{ ok: true }> {
  return request(
    `/entities/${id}/lists/${kind}`,
    { parse: (r) => r as { ok: true } },
    {
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );
}

export function removeEntityFromList(id: string, kind: ListKind): Promise<{ ok: true }> {
  return request(
    `/entities/${id}/lists/${kind}`,
    { parse: (r) => r as { ok: true } },
    {
      method: 'DELETE',
    },
  );
}

// --- Named watchlists ------------------------------------------------
//
// Multiple per user. Entities can be on N watchlists at once (tagging).
// Independent of the Rejected list — adding to a watchlist doesn't
// change rejected state and vice versa.

export interface WatchlistRow {
  id: string;
  name: string;
  /** 'watchlist' (default) | 'rollup' (a Roll-Up Foundation buy-list). */
  kind: 'watchlist' | 'rollup';
  createdAt: string;
  updatedAt: string;
  entityCount: number;
}

// --- Roll-Up Simulator ------------------------------------------------

export interface RollupMember {
  kind: 'real' | 'synthetic';
  id: string;
  name: string | null;
  subsegment: string | null;
  countryCode: string | null;
  region: string | null;
  turnoverEur: number | null;
  /** Modelled revenue confidence P-value (e.g. "P80", "P100" = reported); null
   *  for synthetics and unmodelled reals. */
  revenueConfidence: string | null;
  employees: number | null;
  sites: number;
  /** economic group the (real) member already belongs to — can't acquire an owned opco. */
  ownedBy: string | null;
  /** Every geocoded site for this member — the combined catchment is the union
   *  of all of them (a multi-site operator contributes its whole footprint, not
   *  one representative point). Site-less members fall back to a region centroid. */
  siteCoords: Array<{ lat: number; lng: number }>;
  /** First/representative site (siteCoords[0]) — kept for convenience. */
  latitude: number | null;
  longitude: number | null;
}

export interface RollupCombine {
  opcos: number;
  realCount: number;
  syntheticCount: number;
  ownedCount: number;
  revenueEur: number;
  /** The filed-accounts and modelled halves of revenueEur. Nullish so the web
   *  tolerates an older API during the deploy window. They do not add up to
   *  revenueEur: a hand-entered synthetic member's revenue is neither. */
  revenueReportedEur?: number | null;
  revenueEstimatedEur?: number | null;
  employees: number;
  sites: number;
  subsegments: string[];
  countries: string[];
  regions: string[];
}

export interface RollupConcentration {
  market: { country: string; subsegment: string };
  /** null = national scope; a region name = scoped to that region. */
  region: string | null;
  marketRevEurBefore: number;
  marketRevEurAfter: number;
  groupsBefore: number;
  /** Top-5 group share, 0..1. */
  cr5Before: number;
  cr5After: number;
  /** Herfindahl index, 0..10000. */
  hhiBefore: number;
  hhiAfter: number;
  rollupRevEur: number;
  rollupShare: number;
  rollupRank: number;
}

export interface RollupResult {
  foundation: { id: string; name: string; kind: string };
  members: RollupMember[];
  combine: RollupCombine;
  /** National (country × primary sub-segment) concentration. */
  concentration: RollupConcentration | null;
  /** Same, scoped to the buy-list's primary region; `thin` when the region has
   *  too few groups to read confidently. null when no region resolved. */
  concentrationRegional: (RollupConcentration & { thin: boolean }) | null;
}

export interface SyntheticMemberInput {
  name: string;
  subsegment?: string | null;
  countryCode?: string | null;
  region?: string | null;
  turnoverEur?: number | null;
  employees?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  /** Optional postcode area (e.g. "BL1") — the server places the pin at its
   *  centroid; absent → the region centroid. */
  locator?: string | null;
  note?: string | null;
}

export interface WatchlistEntityRow {
  id: string;
  countryCode: string;
  registryId: string;
  legalName: string | null;
  subSegment: string | null;
  city: string | null;
  scopeFlag: ScopeFlag;
  addedAt: string;
  latestFinancials?: {
    revenue: string | null;
    totalAssets: string | null;
    equity: string | null;
    creditors: string | null;
    employeeCount: number | null;
  } | null;
}

export function listWatchlists(kind?: 'watchlist' | 'rollup'): Promise<{ data: WatchlistRow[] }> {
  const qs = kind ? `?kind=${kind}` : '';
  return request(`/watchlists${qs}`, { parse: (r) => r as { data: WatchlistRow[] } });
}

export function createWatchlist(
  name: string,
  kind?: 'watchlist' | 'rollup',
): Promise<{ data: WatchlistRow }> {
  return request(
    '/watchlists',
    { parse: (r) => r as { data: WatchlistRow } },
    { method: 'POST', body: JSON.stringify(kind ? { name, kind } : { name }) },
  );
}

/** Pro-forma combine for a Roll-Up Foundation (real members + synthetic). */
export function fetchRollup(watchlistId: string): Promise<RollupResult> {
  return request(
    '/rollup',
    { parse: (r) => r as RollupResult },
    { method: 'POST', body: JSON.stringify({ watchlistId }) },
  );
}

// --- Platform Builder (top-down portfolio constructor) ----------------------

export type BuilderObjective =
  | 'gettability'
  | 'fewestDeals'
  | 'tightestCluster'
  | 'diversifyRegion';
export type BuilderAnchorMode = 'anchor-led' | 'aggregation';

export interface PlatformBuilderQuery {
  country: string;
  subsegment: string;
  region?: string | null;
  targetEur: number;
  objectives: BuilderObjective[];
  anchorMode: BuilderAnchorMode;
  /** Minimum revenue (EUR) for a platform anchor; defaults to 25% of target. */
  anchorMinRevEur?: number;
  maxEntities?: number;
  minRevEur?: number;
  /** Optional operator-size band, on revenue-per-site (EUR). */
  minRevPerSiteEur?: number;
  maxRevPerSiteEur?: number;
  /** When anchor-led and the user has confirmed a platform candidate. */
  platformEntityId?: string;
  /** Entities the user asked to replace — dropped so the fill picks alternatives. */
  excludeEntityIds?: string[];
  /** Constraint: exclude operators in the bottom regulatory-quality band. */
  excludeWeakQuality?: boolean;
  /** Aggregation sizing intent — pin the cluster's scale by either the target
   *  number of operators OR the typical operator size (reciprocal via the
   *  target). Absent = auto (pool median). */
  aggTargetCount?: number;
  aggAvgRevEur?: number;
  /** Buyer context (conditions layer) — both tenant-scoped server-side. With
   *  either attached, conditions speak buyer-relative; the selected portfolio's
   *  holdings are excluded from the buy-list and seed gap/overlap logic. */
  mandateId?: string | null;
  portfolioId?: string | null;
}

export interface PlatformBuilderItem {
  entityId: string;
  name: string | null;
  role: 'platform' | 'bolton';
  turnoverEur: number;
  /** Revenue-estimate confidence band, "P0".."P100" (P100 = filed/reported). */
  turnoverConfidenceP: string | null;
  /** Revenue provenance (filed / modelled / …). */
  turnoverBasis: string | null;
  /** Relative regulatory-quality band; null / 'Not rated' = no signal. */
  qualityTier: string | null;
  region: string | null;
  sellerReadiness: number | null;
  originationSignal: number | null;
  /** Compact "why it's here" — the ranking decomposition for this pick (also the
   *  explanation for a target swapped in via Replace). */
  reason: string;
  /** Region build only: kept despite the footprint not being confirmable
   *  in-region (office-located, or sites whose region isn't geocoded). */
  regionUnverified: boolean;
  /** Representative site coordinate (null if no geocoded site). */
  latitude: number | null;
  longitude: number | null;
  /** Per-target acquisition-language conditions (shared conditions engine). */
  conditions?: Condition[];
}

export interface PlatformBuilderSummary {
  assembledEur: number;
  targetEur: number;
  dealCount: number;
  platformCount: number;
  platformName: string | null;
  regions: string[];
  coreRegion: string | null;
  /** Region build: count of targets kept whose in-region footprint couldn't be
   *  verified from regulated sites (drives a clarification note in the panel). */
  regionUnverifiedCount: number;
  feasible: boolean;
  confidence: 'High' | 'Moderate' | 'Low';
  trueConsolidation: 'High' | 'Moderate' | 'Low';
  visibility: 'legible' | 'partial' | 'masked';
  benchmarkBasis: string;
  benchmarkNote: string;
  /** % of subsegment revenue we've resolved into economic groups (diagnostic). */
  resolvedRevenueShare: number;
  poolSize: number;
  poolRevEur: number;
  /** Revenue actually assemblable for this build (anchor + bolt-ons that fit
   *  under it). Equals poolRevEur in aggregation; lower in anchor-led. */
  buildableRevEur: number;
  /** The anchor's revenue (anchor-led), else null — for the inventory message. */
  platformRevEur: number | null;
  /** Median revenue of the eligible pool — explains a high deal count. */
  poolMedianEur: number;
  /** Typical single-hold platform size for this subsegment (display range). */
  typicalBuildLabel: string;
  /** Deal count is far above a realistic single-hold platform. */
  oversized: boolean;
  /** anchor-led requested but no independent ≥25% of target — built as aggregation. */
  anchorFallback: boolean;
  /** Aggregation comparability band (null in anchor-led). center = the size the
   *  cluster is built around; lo/hi = the admitted member range (hi null if
   *  unbounded). */
  comparability: { centerEur: number; loEur: number; hiEur: number | null } | null;
  /** Whether the regulatory-quality filter was requested. */
  qualityGateApplied: boolean;
  /** Whether this market/subsegment carries a quality signal at all — false ⇒ the
   *  quality filter is inert and the panel shows a neutral caveat. */
  qualitySignalAvailable: boolean;
}

export interface PlatformBuilderResult {
  portfolio: PlatformBuilderItem[];
  /** anchor-led only: the 3 candidate platforms to confirm. */
  platformCandidates?: Array<{ entityId: string; name: string | null; turnoverEur: number }>;
  summary: PlatformBuilderSummary;
  rationale: string;
  /** Build-level conditions (shared conditions engine). */
  conditions?: Condition[];
  /** Whether conditions were computed buyer-relative (mandate/portfolio
   *  attached) or from the Prescription form alone. */
  contextMode?: 'buyer_aware' | 'form_context';
}

// ── Investment Memo narrative layer ─────────────────────────────────────────
export interface MemoNarrativeInput {
  factCorpus: string[];
  allowedNames: string[];
  sections: Array<{ key: string; text: string }>;
  contextMode: 'buyer_aware' | 'form_context';
  mandateName?: string | null;
  portfolioName?: string | null;
  subsegment: string;
  geo: string;
}
export interface MemoNarrativeResult {
  /** Only the sections that survived server-side fact validation. */
  sections: Array<{ key: string; text: string }>;
  rejectedKeys: string[];
  model: string;
}

/** Ask the API's drafting layer to redraft the memo's deterministic sections.
 *  Throws on 503 (no key configured) and any failure — callers catch and keep
 *  the deterministic prose. */
export function draftMemoNarrative(input: MemoNarrativeInput): Promise<MemoNarrativeResult> {
  return request(
    '/memo-narrative',
    { parse: (r) => r as MemoNarrativeResult },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

/** Construct a candidate buy-list for a revenue target + market (pure compute). */
export function fetchPlatformBuilder(q: PlatformBuilderQuery): Promise<PlatformBuilderResult> {
  return request(
    '/platform-builder',
    { parse: (r) => r as PlatformBuilderResult },
    { method: 'POST', body: JSON.stringify(q) },
  );
}

// --- FX (display currency) --------------------------------------------------

export interface FxRatesLatest {
  /** currency code → rate_to_eur (EUR value of one unit). */
  rates: Record<string, number>;
  /** ISO timestamp the latest-spot rows were last refreshed, or null. */
  refreshedAt: string | null;
}

export function fetchFxRatesLatest(): Promise<FxRatesLatest> {
  return request('/fx-rates/latest', { parse: (r) => r as FxRatesLatest });
}

// --- Data freshness (footer "updated" stamp) --------------------------------

export interface DataFreshness {
  /** Per-market recompute recency (markets refresh independently). */
  markets: Array<{
    country: string;
    metrics: string | null;
    originationSignal: string | null;
    sellerReadiness: string | null;
  }>;
  /** Latest-spot FX refresh timestamp. */
  fxRefreshedAt: string | null;
  /** Per-source ingest staleness (hourly digest); a frozen lastIngestedAt IS
   *  the rot signal. Empty until the first digest run. */
  sources?: Array<{
    country: string;
    source: string;
    lastIngestedAt: string;
    rows90d: number;
  }>;
  /** Latest weekly model-backtest verdict per market (locked ±50% band). */
  calibrationDrift?: Array<{
    country: string;
    computedAt: string;
    nBacktest: number;
    shareWithinBand: number;
    claimedP: string | null;
    driftFlag: boolean;
  }>;
  /** Scheduled jobs whose MOST RECENT run failed. `lastOkAt` null means the
   *  job has not succeeded once inside pg-boss's retention window — i.e. it is
   *  not flaky, it is dead. Empty array is the healthy state. */
  failingJobs?: Array<{
    name: string;
    lastRunAt: string;
    lastOkAt: string | null;
  }>;
  /** Per-market INGEST freshness against each market's declared cadence.
   *
   *  Distinct from `markets[].metrics`, which is the nightly RECOMPUTE and
   *  runs whether or not source data moved — on 2026-09-13 it showed 26 of 27
   *  markets updated "today" while twenty-one had a median row age of 34-66
   *  days. `status: 'unknown'` means no ingest run has ever been recorded and
   *  must never render as fresh. */
  refresh?: Array<{
    market: string;
    /** `blocked`: a scheduled source for this market cannot run (input or
     *  credential not configured) — see `blockedSources`. */
    status: 'fresh' | 'due' | 'stale' | 'unknown' | 'blocked';
    lastIngestAt: string | null;
    lastIngestSource: string | null;
    ageDays: number | null;
    cadenceDays: number | null;
    alarmAfterDays: number | null;
    mechanism: 'cron' | 'manual' | 'none' | null;
    note: string | null;
    /** Sources whose newest run was skipped for a missing prerequisite. */
    blockedSources?: Array<{ source: string; reason: string; since: string }>;
  }>;
  /** Deletes, changing updates and truncates captured from PROTECTED tables
   *  (ownership, specialisms, crosswalks, geocodes, verdicts, human input) in
   *  the last `windowDays` — migrations 0182/0183. Capture makes a loss
   *  recoverable; this block exists so it is also NOTICED. Empty when the DB
   *  predates 0182. */
  protectedChanges?: {
    windowDays: number;
    byTable: Array<{
      table: string;
      op: 'D' | 'U' | 'T';
      rows: number;
      /** Distinct txids — one per destructive operation. */
      operations: number;
      restoredRows: number;
      latestAt: string;
    }>;
    /** Top 10 operations by rows captured. `txid` is what
     *  `restore_protected(<txid>)` takes; kept as a string (bigint). */
    largestOperations: Array<{
      txid: string;
      tables: string[];
      ops: string;
      rows: number;
      restoredRows: number;
      firstAt: string;
      lastAt: string;
      appName: string | null;
    }>;
  };
}

export function fetchDataFreshness(): Promise<DataFreshness> {
  return request('/data-freshness', { parse: (r) => r as DataFreshness });
}

export function createSyntheticMember(
  watchlistId: string,
  input: SyntheticMemberInput,
): Promise<{ data: unknown }> {
  return request(
    `/watchlists/${watchlistId}/synthetic-members`,
    { parse: (r) => r as { data: unknown } },
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function deleteSyntheticMember(
  watchlistId: string,
  memberId: string,
): Promise<{ ok: true }> {
  return request(
    `/watchlists/${watchlistId}/synthetic-members/${memberId}`,
    { parse: (r) => r as { ok: true } },
    { method: 'DELETE' },
  );
}

export function renameWatchlist(id: string, name: string): Promise<{ data: WatchlistRow }> {
  return request(
    `/watchlists/${id}`,
    { parse: (r) => r as { data: WatchlistRow } },
    { method: 'PATCH', body: JSON.stringify({ name }) },
  );
}

export function deleteWatchlist(id: string): Promise<{ ok: true }> {
  return request(`/watchlists/${id}`, { parse: (r) => r as { ok: true } }, { method: 'DELETE' });
}

export function getWatchlistEntities(id: string): Promise<{ data: WatchlistEntityRow[] }> {
  return request(`/watchlists/${id}/entities`, {
    parse: (r) => r as { data: WatchlistEntityRow[] },
  });
}

export function addEntityToWatchlist(id: string, entityId: string): Promise<{ ok: true }> {
  return request(
    `/watchlists/${id}/entities`,
    { parse: (r) => r as { ok: true } },
    { method: 'POST', body: JSON.stringify({ entityId }) },
  );
}

export function addEntitiesToWatchlist(
  id: string,
  entityIds: string[],
): Promise<{ ok: true; added: number }> {
  return request(
    `/watchlists/${id}/entities/bulk`,
    { parse: (r) => r as { ok: true; added: number } },
    { method: 'POST', body: JSON.stringify({ entityIds }) },
  );
}

export function removeEntityFromWatchlist(id: string, entityId: string): Promise<{ ok: true }> {
  return request(
    `/watchlists/${id}/entities/${entityId}`,
    { parse: (r) => r as { ok: true } },
    { method: 'DELETE' },
  );
}

// Which of the user's watchlists is this entity currently on? Returns
// just the watchlist ids — the caller already has the full watchlist
// list (with names) from listWatchlists().
export function getEntityWatchlists(id: string): Promise<{ data: Array<{ watchlistId: string }> }> {
  return request(`/entities/${id}/watchlists`, {
    parse: (r) => r as { data: Array<{ watchlistId: string }> },
  });
}

// --- Landing-tab agents ---
//
// Three endpoints fed by services/landing-agents. Records are persisted
// in agent-specific tables (ma_events, operating_pressure_signals,
// demand_side_signals); the panel UI just renders the rows.

export type MaEventType = 'acquisition' | 'investment' | 'sale_process' | 'divestment' | 'distress';

export type MaEventStage = 'announced' | 'closed' | 'withdrawn';

export type MaBuyerType = 'pe' | 'consolidator' | 'strategic' | 'other' | 'unknown';

export type MaSubSegment =
  | 'dental'
  | 'veterinary'
  | 'optical'
  | 'pharmacy_retail'
  | 'care_homes'
  | 'hospitals_clinics'
  | 'gp_general_medical'
  | 'specialty_medical'
  | 'mental_health'
  | 'fertility'
  | 'diagnostics_labs'
  | 'audiology'
  | 'healthcare_it';

export type ConfidenceTier = 'high' | 'medium' | 'low';

export type EntityWatchlistRole = 'key_buyer' | 'key_consolidator' | 'key_strategic';

export interface MaEventRow {
  id: string;
  publicId: string;
  eventType: MaEventType;
  stage: MaEventStage | null;
  eventDate: string | null;
  announcedDate: string | null;
  closeDate: string | null;
  detectedAt: string;
  buyerName: string | null;
  buyerType: MaBuyerType | null;
  buyerEntityId: string | null;
  buyerFirmId: string | null;
  buyerIsKnownFirm: boolean | null;
  sellerName: string | null;
  targetName: string;
  targetEntityId: string | null;
  targetCountry: string | null;
  targetSubSegment: MaSubSegment | null;
  targetSubSegmentSecondary: string[] | null;
  dealValue: string | null;
  dealCurrency: string | null;
  dealValueEur: string | null;
  shortDescription: string | null;
  tags: string[] | null;
  advisers: { sellSide?: string[]; buySide?: string[] } | null;
  fieldConfidences: Record<string, ConfidenceTier> | null;
  modelVersion: number | null;
  linkedEventIds: string[] | null;
  sources: Array<{ url: string; publication: string | null; headline: string | null }>;
  sourceCount: number;
  confidence: ConfidenceTier | null;
  /** Watchlist role of the entity matched against the buyer name (brief
   *  § 7.5). Null when the buyer didn't resolve or isn't on a watchlist. */
  buyerWatchlistRole: EntityWatchlistRole | null;
  /** Same for the target — rare but possible for consolidators that
   *  are themselves acquired. */
  targetWatchlistRole: EntityWatchlistRole | null;
  /** Per-source disclosed deal facts (brief § 11.7). The panel groups
   *  these client-side to detect disagreements on dealValue / buyerName
   *  and surface "Sources disagree" notes in the row expansion. */
  sourceFacts: Array<{
    publisher: string | null;
    dealValue: string | null;
    dealCurrency: string | null;
    buyerName: string | null;
    capturedDate: string;
    sourceUrl: string;
  }>;
  /** Linked events resolved from linked_event_ids (brief § 4.3 / Stage C).
   *  Each is a small projection — the panel uses these for the
   *  "linked announce / closed" affordance in row expansion. */
  linkedEvents: Array<{
    id: string;
    publicId: string;
    eventType: MaEventType;
    stage: MaEventStage | null;
    eventDate: string | null;
    shortDescription: string | null;
  }>;
}

// Pressure-event source (brief §3.2) — one row per source-of-truth for
// a given event. Multi-source coverage merges into one pressure event
// with multiple source rows. The full detail is exposed in the panel's
// row-expansion view.
export interface PressureSourceRow {
  id: string;
  eventId: string;
  sourceUrl: string;
  sourceType:
    | 'regulator_publication'
    | 'industry_body_publication'
    | 'government_publication'
    | 'specialist_press'
    | 'trade_press'
    | 'court_decision'
    | 'licensed_feed';
  sourcePublisher: string;
  sourceRegionalFocus: 'european' | 'global' | 'national' | 'regional' | 'multi_regional' | null;
  sourceLanguage: string | null;
  sourcePublishDate: string | null;
  sourceCapturedDate: string;
  sourceTitle: string | null;
  sourceExcerpt: string | null;
  sourceConfidence: 'high' | 'medium' | 'low' | null;
  sourceRelevanceTier: 'inherent' | 'section' | 'keyword_trigger';
}

// Pressure event — v1.1 shape (corryk_operating_pressure.md §3). Maps
// 1:1 to the pressure_events table columns plus a joined `sources`
// array. The legacy v1.0 panel kept a stub OperatingPressureRow with
// optional fields and em-dash placeholders; that's gone — the API now
// emits a strict v1.1 shape.
export interface OperatingPressureRow {
  id: string;
  publicId: string;

  // Dating.
  eventDate: string | null;
  recordedDate: string;
  effectiveDate: string | null;

  // Categorisation.
  pressureCategory: 'regulatory' | 'financial' | 'structural';
  pressureSubcategory:
    | 'inspection_enforcement'
    | 'contract_reform'
    | 'authorisation_licensing'
    | 'workforce_regulation'
    | 'market_study_review'
    | 'reimbursement_change'
    | 'tax_succession'
    | 'cost_inflation'
    | 'subsidy_funding'
    | 'workforce_shortage'
    | 'demographic_demand'
    | 'digitalisation'
    | 'esg_technology';

  description: string;

  affectedCountries: string[];
  affectedSubSegments: string[];

  severity: 'high' | 'medium' | 'low';
  severityReasoning: string | null;
  severityConfidence: 'high' | 'medium' | 'low' | null;

  direction: 'new' | 'rising' | 'easing';
  timeHorizon: 'immediate' | '6_months' | '12_plus_months' | 'tbd';

  stage: 'proposed' | 'consulted' | 'enacted' | 'in_force' | 'withdrawn';

  issuingAuthority: string;
  policyReference: string | null;

  tags: string[];
  linkedEventIds: string[];

  confidence: 'high' | 'medium' | 'low' | null;
  modelVersion: number | null;

  reviewStatus: 'auto_approved' | 'pending_review' | 'approved' | 'rejected';
  panelVisibility: 'visible' | 'hidden';

  firstDetectedAt: string;
  lastUpdatedAt: string;

  sources: PressureSourceRow[];
}

export interface AgentListQuery {
  countries?: string[];
  subSegments?: string[];
  limit?: number;
}

function agentParams(q: AgentListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.countries?.length) p.set('countries', q.countries.join(','));
  if (q.subSegments?.length) p.set('sub_segments', q.subSegments.join(','));
  if (q.limit !== undefined) p.set('limit', String(q.limit));
  return p;
}

export function listOperatingPressure(
  q: AgentListQuery = {},
): Promise<{ data: OperatingPressureRow[] }> {
  const qs = agentParams(q).toString();
  return request(`/operating-pressure${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as { data: OperatingPressureRow[] },
  });
}

export interface DemandProjectionRow {
  countryCode: string;
  /** NULL = national row; GB carries English regions + devolved nations. */
  region: string | null;
  ageBand: 'total' | '65plus' | '80plus';
  baseYear: number;
  baseValue: number;
  horizonYear: number;
  projectedValue: number;
  /** Growth vs base year, percent (one decimal), null when base is zero. */
  pctChange: number | null;
  source: string;
}

/** Population-projection demand outlook (Eurostat EUROPOP2023 + ONS 2022). */
export function fetchDemandShifts(): Promise<{ data: DemandProjectionRow[] }> {
  return request('/demand-shifts', {
    parse: (r) => r as { data: DemandProjectionRow[] },
  });
}

// --- Facilities map (GET /facilities) ---------------------------------
//
// One point per geocoded CQC location. The Map panel fetches the whole
// filtered set in a single request (limit defaults to the full universe
// server-side) and clusters client-side, so pan/zoom never refetches.

export interface FacilityPoint {
  id: string;
  /** Null until the CQC provider→entity crosswalk resolves this site. */
  entityId: string | null;
  lat: number | null;
  lng: number | null;
  name: string | null;
  /** Raw CQC service-type strings — the per-site sub-segment. */
  serviceTypes: string[];
  rating: string | null;
  region: string | null;
  /** 'site' = geocoded facility (GB/FR); 'office' = registered-office
   *  coordinates from the entity row (IT/HR/BG/LV/SK/GR/LT) — one pin per
   *  company, office-grade precision. */
  source: 'site' | 'office';
}

export interface FacilitiesResponse {
  points: FacilityPoint[];
  total: number;
  /** True when `total` is the server's count cap, i.e. the real total is ≥
   *  `total` — render it as "{total}+". */
  totalCapped?: boolean;
  limit: number;
  offset: number;
}

export interface FacilitiesFacets {
  country: string[];
  subsegment: string[];
  ownershipType: string[];
}

export interface FacilitiesQuery {
  country?: readonly string[];
  subsegment?: readonly string[];
  /** Fine sub-segment tier (primary_specialty tokens). One hierarchical filter
   *  with `subsegment` in the UI; the API OR-s the two. */
  specialty?: readonly string[];
  ownershipType?: readonly string[];
  /** Filter sites by their owning company's estimated turnover (EUR). */
  turnoverEurMin?: number;
  turnoverEurMax?: number;
}

export function fetchFacilities(q: FacilitiesQuery = {}): Promise<FacilitiesResponse> {
  const params = new URLSearchParams();
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  if (q.specialty?.length) params.set('specialty', q.specialty.join(','));
  if (q.ownershipType?.length) params.set('ownershipType', q.ownershipType.join(','));
  if (q.turnoverEurMin !== undefined) params.set('turnoverEurMin', String(q.turnoverEurMin));
  if (q.turnoverEurMax !== undefined) params.set('turnoverEurMax', String(q.turnoverEurMax));
  const qs = params.toString();
  return request(`/facilities${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as FacilitiesResponse,
  });
}

export function fetchFacilitiesFacets(): Promise<FacilitiesFacets> {
  return request('/facilities/facets', { parse: (r) => r as FacilitiesFacets });
}

// --- Bolt-On screening (GET /bolt-on) ---------------------------------
//
// "Within reach of THIS point, which independent operators of <subsegment>
// could be acquired to add density, and who is already rolling up here?"
// One request per (seed, subsegment, band); the map plots every site, the
// side list is the ranked candidate operators (targets only).

export interface BoltOnPoint {
  id: string;
  entityId: string | null;
  lat: number;
  lng: number;
  name: string | null;
  countryCode: string | null;
  subsegment: string | null;
  klass: BoltOnClass;
  /** Resolved economic group (competitor-layer colour); null for independents. */
  group: string | null;
  turnoverScreener: number | null;
  turnoverEur: number | null;
  turnoverConfidenceP: string | null;
  turnoverBasis: string | null;
  ownershipType: string | null;
  /** Target whose independence rests on weak/absent ownership data. */
  independenceProxy: boolean;
  owner75Age: number | null;
  isQuarantined: boolean;
  distanceKm: number;
}

export interface BoltOnBoundary {
  mode: CatchmentMode;
  lat: number;
  lng: number;
  radiusKm: number;
  band: number;
  /** Isochrone outline as [lat,lng] pairs (Leaflet order); present in isochrone mode. */
  polygon?: [number, number][];
}

export interface BoltOnResponse {
  points: BoltOnPoint[];
  candidates: BoltOnPoint[];
  /** Market the seed coordinate falls in, resolved server-side from census_area
   *  — lets a hand-dropped pin profile against the right country. */
  market?: string | null;
  boundary: BoltOnBoundary;
  counts: { target: number; competitor: number; neutral: number };
}

// --- census / catchment details ---
export interface CensusSupplyRow {
  available?: null;
  note?: string;
  profession?: string;
  density_per_100k?: number;
  density_basis?: string | null;
  est_clinicians_in_catchment?: number;
  vs_national_pct?: number | null;
  demand_adjusted_density?: number | null;
  remuneration_x_avg_wage?: number | null;
  remuneration_usd_ppp?: number | null;
  read?: string;
}
export interface CensusResponse {
  people: number;
  households: number | null;
  /** National income percentile, 0–100 (50 = market median). Normalises across
   *  markets whose income source is currency (£/€) OR an ordinal deprivation rank. */
  income_percentile: number | null;
  income_basis: string | null;
  age_structure: Record<string, number | null>;
  sex: { female: number; male: number; female_pct: number; male_pct: number } | null;
  demand_index: number | null;
  pets:
    | {
        est_dogs: number;
        est_cats: number;
        dog_owning_households: number;
        cat_owning_households: number;
        basis: string;
      }
    | { note: string };
  clinical_supply: Record<string, CensusSupplyRow>;
  meta: {
    areas_in_catchment: number;
    market: string;
    region: string | null;
    provenance: Record<string, string>;
  };
}

export function fetchCensus(body: {
  polygon: unknown;
  market: string;
  country?: string;
  region?: string | null;
}): Promise<CensusResponse> {
  return request(
    '/census',
    { parse: (r) => r as CensusResponse },
    { method: 'POST', body: JSON.stringify(body) },
  );
}

// --- Dossier: single-company competitive concentration ---------------

export interface DossierCompetition {
  subsegment: string;
  country: string;
  /** Region the concentration is scoped to (null when it fell back to national). */
  region: string | null;
  scope: 'regional' | 'national';
  marketRevEur: number;
  groups: number;
  /** Top-5 group share (0..1) + Herfindahl index (0..10000). */
  cr5: number;
  hhi: number;
  thisRank: number | null;
  thisShare: number;
  top: Array<{ label: string; revEur: number; share: number; isThis: boolean }>;
}

export function fetchCompanyCompetition(
  entityId: string,
): Promise<{ competition: DossierCompetition | null }> {
  return request(`/companies/${entityId}/competition`, {
    parse: (r) => r as { competition: DossierCompetition | null },
  });
}

// --- Alerts / monitoring feed ----------------------------------------

export interface AlertItem {
  id: string;
  entityId: string;
  /** seller_readiness_up|_down | origination_up|_down | accounts_overdue | new_charge | status_change */
  type: string;
  title: string;
  detail: string | null;
  createdAt: string;
  readAt: string | null;
  name: string | null;
  subsegment: string | null;
}

export function fetchAlerts(): Promise<{ data: AlertItem[]; unread: number }> {
  return request('/alerts', { parse: (r) => r as { data: AlertItem[]; unread: number } });
}

export function markAlertsRead(body: { ids?: string[]; all?: boolean }): Promise<{ ok: true }> {
  return request(
    '/alerts/read',
    { parse: (r) => r as { ok: true } },
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export interface BoltOnQuery {
  lat: number;
  lng: number;
  subsegment: string;
  band: number;
  mode?: CatchmentMode;
  /** Travel mode → routing profile + radius speed (drive | walk). */
  travel?: TravelMode;
  /** Revenue-estimate band (EUR) on the target layer. */
  turnoverEurMin?: number;
  turnoverEurMax?: number;
}

export function fetchBoltOn(q: BoltOnQuery): Promise<BoltOnResponse> {
  const params = new URLSearchParams({
    lat: String(q.lat),
    lng: String(q.lng),
    subsegment: q.subsegment,
    band: String(q.band),
  });
  if (q.mode) params.set('mode', q.mode);
  if (q.travel) params.set('travel', q.travel);
  if (q.turnoverEurMin !== undefined) params.set('turnoverEurMin', String(q.turnoverEurMin));
  if (q.turnoverEurMax !== undefined) params.set('turnoverEurMax', String(q.turnoverEurMax));
  return request(`/bolt-on?${params.toString()}`, { parse: (r) => r as BoltOnResponse });
}

// --- multi-point catchment (Roll-Up Foundation coverage view) --------

/** One member's catchment. `catchment` is null for a B2B-distributor member
 *  (no residential catchment). Same shape as a single /bolt-on response. */
export interface BoltOnMemberCatchment {
  lat: number;
  lng: number;
  subsegment: string | null;
  catchment: BoltOnResponse | null;
}

export interface BoltOnMultiQuery {
  members: Array<{ lat: number; lng: number; subsegment: string }>;
  /** Uniform travel-time override; omit → each member uses its own subsegment×urbanity catchment. */
  band?: number;
  travel?: TravelMode;
  turnoverEurMin?: number;
  turnoverEurMax?: number;
}

export function fetchBoltOnMulti(
  q: BoltOnMultiQuery,
): Promise<{ members: BoltOnMemberCatchment[] }> {
  return request(
    '/bolt-on/multi',
    { parse: (r) => r as { members: BoltOnMemberCatchment[] } },
    { method: 'POST', body: JSON.stringify(q) },
  );
}

export interface BoltOnFootprint {
  lat: number | null;
  lng: number | null;
  subsegment: string | null;
  /** The seed company's market + region — broadcast to /census so the catchment
   *  profiles against the right country's census tables and its regional
   *  clinician density, rather than being guessed from the pin's longitude. */
  countryCode?: string | null;
  region?: string | null;
  siteCount: number;
  /** Where the seed came from: 'sites' = geocoded operating sites; 'office' =
   *  registered-office postcode fallback (approximate); null = unresolved. */
  source?: 'sites' | 'office' | null;
  /** Local population density (people/km²) around the site — urbanity signal
   *  for the subsegment-specific catchment. null where not resolvable. */
  localDensityPerKm2?: number | null;
  /** Every geocoded operating site (not just the centroid) — so a dispersed
   *  multi-site operator is shown as all its sites, not one misleading midpoint. */
  sites?: Array<{ lat: number; lng: number; name: string | null }>;
}

/** Seed for "Find bolt-ons near this company" — centroid of the company's
 *  geocoded sites + its subsegment. */
export function fetchBoltOnFootprint(entityId: string): Promise<BoltOnFootprint> {
  return request(`/bolt-on/footprint/${encodeURIComponent(entityId)}`, {
    parse: (r) => r as BoltOnFootprint,
  });
}

// ── Consolidators ("Who's Who") ──────────────────────────────────────────────

export interface ConsolidatorPlatform {
  group: string;
  sponsor: string | null;
  backerType: string | null;
  sponsorStatus: string | null;
  confidence: string | null;
  opcos: number;
  sites: number;
  subsegments: string[];
  countries: string[];
  regions: string[];
  revenueEur: number;
  /** Market-intel row (no financial entities) — opcos/revenue dash out. */
  curated?: boolean;
}

export interface ConsolidatorsResponse {
  platforms: ConsolidatorPlatform[];
  countries: string[];
}

export interface ConsolidatorsQuery {
  country?: string[];
  subsegment?: string[];
  region?: string[];
  backerType?: string[];
}

export function fetchConsolidators(q: ConsolidatorsQuery = {}): Promise<ConsolidatorsResponse> {
  const params = new URLSearchParams();
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  if (q.region?.length) params.set('region', q.region.join(','));
  const qs = params.toString();
  return request(`/consolidators${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as ConsolidatorsResponse,
  });
}

/** Per-market consolidator depth — which markets have resolved economic groups
 *  to show. Drives the Prevalence market picker so a market with no ownership
 *  resolution is disabled and LABELLED rather than silently absent (or worse,
 *  present and empty, which reads as "no consolidation here"). */
export interface ConsolidatorCoverage {
  markets: Array<{
    country: string;
    groups: number;
    groupedEntities: number;
    hasData: boolean;
  }>;
  generatedAt: string;
}

export function fetchConsolidatorCoverage(): Promise<ConsolidatorCoverage> {
  return request('/consolidators/coverage', { parse: (r) => r as ConsolidatorCoverage });
}

// ── Ownership Network (multi-group explorer) ─────────────────────────────────

export interface OwnershipMember {
  id: string;
  name: string;
  subsegment: string | null;
  specialty: string | null;
  country: string;
  city: string | null;
  revenueEur: number;
  /** Holdco (corporate_parent) this member sits under — null = held directly. */
  holdco: string | null;
}
export interface OwnershipHoldco {
  name: string | null;
  country: string;
  opcos: number;
  revenueEur: number;
}
export type OwnershipClass =
  | 'pe'
  | 'strategic'
  | 'listed'
  | 'family'
  | 'infra'
  | 'sovereign'
  | 'reit'
  | 'cooperative'
  | 'foundation'
  | 'independent';
export interface OwnershipGroupNode {
  id: string;
  label: string;
  subsegments: string[];
  dominantSubsegment: string | null;
  specialties: string[];
  opcos: number;
  revenueEur: number;
  countries: string[];
  regions: string[];
  backer: string | null;
  backerType: string | null;
  sponsorStatus: string | null;
  confidence: string | null;
  ownershipClass: OwnershipClass;
  holdcos: OwnershipHoldco[];
  members: OwnershipMember[];
}
export interface OwnershipNetworkResponse {
  groups: OwnershipGroupNode[];
  summary: {
    groupCount: number;
    sponsorCount: number;
    opcosGrouped: number;
    revenueGroupedEur: number;
  };
}
export interface OwnershipNetworkQuery {
  country?: string[];
  subsegment?: string[];
  specialty?: string[];
  ownership?: string[];
  region?: string[];
  minOpcos?: number;
  memberCap?: number;
}

export function fetchOwnershipNetwork(
  q: OwnershipNetworkQuery = {},
): Promise<OwnershipNetworkResponse> {
  const params = new URLSearchParams();
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  if (q.specialty?.length) params.set('specialty', q.specialty.join(','));
  if (q.ownership?.length) params.set('ownership', q.ownership.join(','));
  if (q.region?.length) params.set('region', q.region.join(','));
  if (q.minOpcos != null) params.set('minOpcos', String(q.minOpcos));
  if (q.memberCap != null) params.set('memberCap', String(q.memberCap));
  const qs = params.toString();
  return request(`/ownership-network${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as OwnershipNetworkResponse,
  });
}

export interface ConsolidatorSite {
  lat: number;
  lng: number;
  group: string;
  subsegment: string | null;
  backerType: string | null;
}

export interface ConsolidatorSitesResponse {
  sites: ConsolidatorSite[];
  countries: string[];
}

export function fetchConsolidatorSites(
  q: ConsolidatorsQuery = {},
): Promise<ConsolidatorSitesResponse> {
  const params = new URLSearchParams();
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  if (q.backerType?.length) params.set('backerType', q.backerType.join(','));
  const qs = params.toString();
  return request(`/consolidators/sites${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as ConsolidatorSitesResponse,
  });
}

export interface ConsolidatorWhitespaceResponse {
  /** Independent-count per uniform grid cell + the cell's modal region. */
  cells: Array<{ ci: number; cj: number; n: number; region: string | null }>;
  /** Market-Opportunity index (0..100) per region for the chosen subsegment. */
  regionIndex: Record<string, number | null>;
  cellDeg: number;
  countries: string[];
}

export function fetchConsolidatorWhitespace(
  q: ConsolidatorsQuery = {},
): Promise<ConsolidatorWhitespaceResponse> {
  const params = new URLSearchParams();
  if (q.country?.length) params.set('country', q.country.join(','));
  if (q.subsegment?.length) params.set('subsegment', q.subsegment.join(','));
  const qs = params.toString();
  return request(`/consolidators/whitespace${qs ? `?${qs}` : ''}`, {
    parse: (r) => r as ConsolidatorWhitespaceResponse,
  });
}

// --- homes (care-home grain: GET /homes*, apps/api/src/routes/homes.ts) ---
//
// One row per in-scope care place across the 27 markets (care_home_index,
// migration 0291). Lifecycle defaults to operating server-side; the operator is
// scope-checked at read time, so `operator_entity_id` is null whenever the
// linked company is outside the care product scope. Never a raw geo_precision
// label: location precision arrives as `pin_level` (precise | locality).

/** A /homes list row — only the columns the panel asked for are present. */
export type HomeRow = { home_key: string } & Record<string, unknown>;

/** Totals rule: homes AND beds AND distinct operators / groups / landlords. */
export interface HomeTotals {
  homes: number;
  beds_operating: number;
  beds_planned: number;
  homes_with_beds: number;
  operators: number;
  groups: number;
  landlords: number;
}

export interface HomesListResponse {
  rows: HomeRow[];
  total: number;
  totals: HomeTotals;
  limit: number;
  offset: number;
}

export interface HomeFacetValue {
  value: string | null;
  homes: number;
  beds: number;
}

export interface HomeFacetsResponse {
  facets: Record<string, HomeFacetValue[]>;
  totals: HomeTotals;
}

/** Wire query for /homes and /homes/facets. Categorical + array filters are
 *  comma lists; ranges are `min,max` with either side empty. */
export type HomesQuery = Record<string, string | number | undefined>;

function homesQs(q: HomesQuery): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) {
    if (v === undefined || v === '') continue;
    params.set(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function fetchHomes(q: HomesQuery): Promise<HomesListResponse> {
  return request(`/homes${homesQs(q)}`, { parse: (r) => r as HomesListResponse });
}

export function fetchHomeFacets(q: HomesQuery): Promise<HomeFacetsResponse> {
  return request(`/homes/facets${homesQs(q)}`, { parse: (r) => r as HomeFacetsResponse });
}

/** GET /homes/:homeKey — the full row, grouped (identity, care, location, …). */
export interface HomeDetail {
  identity: Record<string, unknown> & { home_key: string; name: string | null };
  care: Record<string, unknown>;
  location: Record<string, unknown>;
  capacity: Record<string, unknown>;
  operator: Record<string, unknown>;
  property: Record<string, unknown>;
  quality: Record<string, unknown>;
  money: Record<string, unknown>;
  hazard: Record<string, unknown>;
  catchment: Record<string, unknown>;
  workforce: Record<string, unknown>;
  development: Record<string, unknown>;
  provenance: Record<string, unknown> & { terms_flags?: string[] | null };
  /** Fields NULL by design on a development row — shown as "not yet operating". */
  not_applicable: string[];
}

export interface HomeLookup {
  home: HomeDetail;
  /** The key the caller asked for, when an alias redirect moved it. */
  movedFrom: string | null;
}

/** Fetch one home, following `{ moved_to }` alias redirects (a development
 *  project that opened → its operating home). Bounded so a bad alias loop
 *  cannot spin. */
export async function fetchHome(homeKey: string): Promise<HomeLookup> {
  let key = homeKey;
  for (let hop = 0; hop < 5; hop++) {
    // ?key=, never the path: ~3k keys contain '/', which the API router splits on even when encoded.
    const r = await request(`/homes/record${homesQs({ key })}`, {
      parse: (x) => x as HomeDetail | { moved_to: string },
    });
    if ('moved_to' in r) {
      key = r.moved_to;
      continue;
    }
    return { home: r, movedFrom: key === homeKey ? null : homeKey };
  }
  throw new ApiError(`alias chain too long for home ${homeKey}`, 508, null);
}

export interface HomeNeighbour {
  home_key: string;
  name: string | null;
  lat: number | null;
  lon: number | null;
  pin_level: string | null;
  beds: number | null;
  setting: string | null;
  care_level: string | null;
  operator_entity_id: string | null;
  operator_name: string | null;
  economic_group: string | null;
  landlord_key: string | null;
  rating: string | null;
  rating_scale: string | null;
  distance_km: number;
}

export interface HomeNeighboursResponse {
  homeKey: string;
  radiusKm: number;
  neighbours: HomeNeighbour[];
}

/** One home on the map (GET /homes/points — the light payload). */
export interface HomePoint {
  home_key: string;
  lat: number;
  lon: number;
  pin_level: string | null;
  name: string | null;
  beds: number | null;
  setting: string | null;
  care_level: string | null;
  lifecycle: string;
  operator_entity_id: string | null;
  economic_group: string | null;
  landlord_key: string | null;
}

export interface HomePointsResponse {
  points: HomePoint[];
  /** More homes match than were sent (the sample is spread across markets). */
  truncated: boolean;
}

export function fetchHomePoints(q: HomesQuery): Promise<HomePointsResponse> {
  return request(`/homes/points${homesQs(q)}`, { parse: (r) => r as HomePointsResponse });
}

/** One current quality signal of a home (care_home_quality_signal), verbatim. A signal, never a grade. */
export interface HomeSignal {
  signal_class: string;
  signal: string;
  direction: 'positive' | 'negative' | 'neutral' | string;
  statement: string;
  publisher: string;
  source: string;
  source_url: string | null;
  observed_on: string | null;
  effective_from: string | null;
  effective_to: string | null;
  valid_until: string | null;
  terms_status: string | null;
}

export interface HomeSignalsResponse {
  homeKey: string;
  signals: HomeSignal[];
}

export function fetchHomeSignals(homeKey: string): Promise<HomeSignalsResponse> {
  return request(`/homes/signals${homesQs({ key: homeKey })}`, {
    parse: (r) => r as HomeSignalsResponse,
  });
}

export function fetchHomeNeighbours(
  homeKey: string,
  radiusKm: number,
  limit = 100,
): Promise<HomeNeighboursResponse> {
  return request(`/homes/neighbours${homesQs({ key: homeKey, radiusKm, limit })}`, {
    parse: (r) => r as HomeNeighboursResponse,
  });
}
